import { z } from "zod";

import { machineTypeSchema } from "../machine-type";
import { machineWithCorrugateTracksWithToleranceSchema } from "../machine-with-corrugate-tracks";

const plcTrackSettingsSchema = z.object({
	installed: z.boolean(),
	materialBrand: z.string().optional(),
	materialWidth: z.number().optional(),
	materialThickness: z.number().optional(),
	isEmpty: z.boolean(),
});

const plcSettingsSchema = z.object({
	track1: plcTrackSettingsSchema.optional(),
	track2: plcTrackSettingsSchema.optional(),
	track3: plcTrackSettingsSchema.optional(),
});

export const pl1MachineSchema = machineWithCorrugateTracksWithToleranceSchema.extend({
	machineType: z.literal(machineTypeSchema.enum.PL1),
	plcSettings: plcSettingsSchema.optional(),
});

// TODO @eb remove this function when the PL1 aligns with our decided upon schemas
export const preparePl1MachineData = (data: unknown): unknown => {
	if (typeof data !== "object" || data === null) {
		return data;
	}

	const input = data as Record<string, unknown>;

	const iotDevice = {
		deviceId: input.iotDeviceId ?? "",
		deviceName: input.iotEdgeDeviceId ?? "",

		hubName: input.iotHubName ?? "",
		moduleId: input.iotEdgeModuleId ?? "",
		moduleVersion: input.edgeModuleVersion ?? "",
		lastProofOfLife: input.lastProofOfLifeTimeStamp,
	};

	let tracks = input.tracks;
	if (Array.isArray(tracks)) {
		tracks = tracks.map((t, i) => {
			if (typeof t !== "object" || t === null) {
				return t;
			}

			const track = t as Record<string, unknown>;

			let assignedMaterial = track.assignedMaterial;
			if (typeof assignedMaterial === "object" && assignedMaterial !== null) {
				const material = assignedMaterial as Record<string, unknown>;

				assignedMaterial = {
					id: material.id ?? "",
					alias: material.alias ?? "",
					width: material.width ?? 0,
					thickness: material.thickness ?? 0,
					quality: material.quality ?? 0,
				};
			} else {
				// If assignedMaterial is null, create default
				assignedMaterial = {
					id: "",
					alias: "",
					width: 0,
					thickness: 0,
					quality: 0,
				};
			}

			return {
				number: track.number ?? 0,
				isEmpty: track.isTrackEmpty ?? false,
				assignedMaterial,
				corrugateTolerance: 5,
				// @ts-expect-error ts2339
				width: input.plcSettings?.[`track${i + 1}`].materialWidth ?? 0,
			};
		});
	}

	const activeStopReason = input.activeStopReason as Record<string, unknown>;
	let stopReason: {
		stopCode: number;
		message: string;
		arguments: number[];
		isError: boolean;
	} = { stopCode: 0, message: "", arguments: [], isError: false };
	if (typeof activeStopReason === "object" && activeStopReason !== null) {
		stopReason = {
			stopCode: (activeStopReason.stopCode as number) ?? 0,
			message: (activeStopReason.message as string) ?? "",
			arguments: [activeStopReason.argument ?? undefined].filter((a) => a !== undefined).map(Number),
			isError: activeStopReason.isError === true,
		};
	}

	let activeWarnings = Array.isArray(input.warnings) ? input.warnings : [];
	activeWarnings = activeWarnings.map((w: Record<string, unknown>) => ({
		message: w.message,
		code: w.code,
		arguments: [w.argument ?? undefined].filter((a) => a !== undefined).map(Number),
	}));

	let workstations = input.workstations;
	if (!Array.isArray(workstations) || workstations.length < 1) {
		const activeErrors = Array.isArray(input.errors) ? input.errors : [];

		workstations = [
			{
				number: 1,
				alarms: activeErrors.map((e: Record<string, unknown>) => ({
					code: e.code,
					message: e.message,
					arguments: [e.argument ?? undefined].filter((a) => a !== undefined).map(String),
				})),
				warnings: activeWarnings.map((w: Record<string, unknown>) => ({
					...w,
					arguments: Array.isArray(w.arguments) ? w.arguments.map(String) : [],
				})),
				stopReason: {
					code: stopReason.stopCode,
					message: stopReason.message,
					arguments: stopReason.arguments.map(String),
					isError: stopReason.isError,
				},
			},
		];
	}

	return {
		...input,
		machineType: "PL1",
		iotDevice,
		stopReason,
		activeWarnings,
		tracks,
		workstations,
	};
};

export type PL1Machine = z.infer<typeof pl1MachineSchema>;
